import React, { useEffect } from 'react';
import { useConfig } from '../contexts/ConfigContext';
import { useLocation } from 'react-router-dom';
import { getEffectiveThemes, generateServiceCardCss } from '../constants/builtinThemes';

/**
 * Composant qui génère dynamiquement les styles CSS basés sur la configuration
 * Ce composant doit être placé à la racine de l'application pour que les styles soient appliqués globalement
 */
const DynamicStylesProvider = ({ children }) => {
  const { config, loading } = useConfig();
  const location = useLocation();

  useEffect(() => {
    if (loading || !config) return;

    // Déterminer le panneau actuel en fonction de l'URL
    const currentPath = location.pathname;
    let currentPanel = 'global';

    if (currentPath.includes('/admin')) {
      currentPanel = 'admin';
      document.body.className = 'admin-panel';
    } else if (currentPath.includes('/counter')) {
      currentPanel = 'counter';
      document.body.className = 'counter-panel';
    } else if (currentPath.includes('/display')) {
      currentPanel = 'display';
      document.body.className = 'display-panel';
    } else if (currentPath.includes('/distributor')) {
      currentPanel = 'distributor';
      document.body.className = 'distributor-panel';
    } else {
      document.body.className = 'global-panel';
    }

    console.log(`DynamicStylesProvider: panneau actuel détecté = ${currentPanel}`);

    // Récupère les styles globaux
    const globalStyles = config.styles?.global || {
      primaryColor: config.ui?.primaryColor || '#2196F3',
      secondaryColor: config.ui?.secondaryColor || '#4CAF50',
      fontFamily: config.ui?.fontFamily || 'Arial, sans-serif',
      backgroundColor: '#f5f5f5',
      logoSize: '4rem'
    };

    // Récupère les styles spécifiques au panneau actuel
    const panelStyles = config.styles?.[currentPanel] || {};

    // Fusionne les styles (le panneau a priorité sur le global)
    const primaryColor = panelStyles.primaryColor || globalStyles.primaryColor;
    const secondaryColor = panelStyles.secondaryColor || globalStyles.secondaryColor;
    const fontFamily = panelStyles.fontFamily || globalStyles.fontFamily;
    const backgroundColor = panelStyles.backgroundColor || globalStyles.backgroundColor;
    const logoSize = panelStyles.logoSize || globalStyles.logoSize || '4rem';
    // Typographie des noms de service. Le DISTRIBUTEUR la lit dans `styles.display`, pas
    // dans `styles.distributor` : c'est déjà ce que fait le distributeur Electron, qui
    // rapatrie `styles.display` du serveur pour que ses cadres soient identiques à ceux de
    // /display. Un seul réglage de console gouverne donc les deux écrans vus par le public
    // — sinon la borne et l'écran d'attente nomment le même service différemment.
    // `styles.distributor` garde le dernier mot s'il porte explicitement la clé.
    const snStyles = currentPanel === 'distributor'
      ? { ...(config.styles?.display || {}), ...panelStyles }
      : panelStyles;
    // Taille du nom de service sur l'affichage (réglable onglet Styles → styles.display.serviceNameSize)
    const serviceNameSize = snStyles.serviceNameSize || '1.8rem';
    // Échelle PAR LANGUE (relative à serviceNameSize), réglable depuis la console
    // (Styles → styles.display.serviceNameScale{Ar,En,Fr}). Défauts = look historique.
    const num = (v, d) => (Number.isFinite(Number(v)) && Number(v) > 0 ? Number(v) : d);
    const snScaleAr = num(snStyles.serviceNameScaleAr, 0.8);
    const snScaleEn = num(snStyles.serviceNameScaleEn, 0.65);
    const snScaleFr = num(snStyles.serviceNameScaleFr, 1);

    // Valeurs de couleur supplémentaires de l'UI
    const darkColor = config.ui?.darkColor || '#0D47A1';
    const lightColor = config.ui?.lightColor || '#E3F2FD';

    // Crée une feuille de style dynamique
    let styleSheet = document.getElementById('dynamic-styles');
    
    if (!styleSheet) {
      styleSheet = document.createElement('style');
      styleSheet.id = 'dynamic-styles';
      document.head.appendChild(styleSheet);
    }

    // Styles CSS dynamiques de base
    const cssRules = `
      :root {
        --primary-color: ${primaryColor};
        --secondary-color: ${secondaryColor};
        --dark-color: ${darkColor};
        --light-color: ${lightColor};
        --font-family: ${fontFamily};
        --background-color: ${backgroundColor};
        --logo-size: ${logoSize};
        --service-name-size: ${serviceNameSize};
        --sn-scale-ar: ${snScaleAr};
        --sn-scale-en: ${snScaleEn};
        --sn-scale-fr: ${snScaleFr};
      }
      
      body {
        font-family: var(--font-family);
        background-color: var(--background-color);
        margin: 0;
        padding: 0;
      }
      
      /* Override de Tailwind pour les couleurs primaires */
      .bg-primary-500 {
        background-color: var(--primary-color) !important;
      }
      
      .bg-primary-600 {
        background-color: var(--dark-color) !important;
      }
      
      .bg-primary-100 {
        background-color: var(--light-color) !important;
      }
      
      .text-primary-500 {
        color: var(--primary-color) !important;
      }
      
      .border-primary-500 {
        border-color: var(--primary-color) !important;
      }
      
      /* Override de Tailwind pour les couleurs secondaires */
      .bg-secondary-500 {
        background-color: var(--secondary-color) !important;
      }
      
      .text-secondary-500 {
        color: var(--secondary-color) !important;
      }
      
      .border-secondary-500 {
        border-color: var(--secondary-color) !important;
      }
      
      /* Logo styling pour tous les panneaux */
      .app-logo {
        height: var(--logo-size);
        width: auto;
        display: inline-block;
      }
    `;
    
    // Apparence des carrés de services (/display) — issue du THÈME ACTIF, CSS généré
    // par @mostajs/themes (forme/typo/taille). Surcharge le display-screen.css du
    // thème-fichier car ce <style> est injecté APRÈS les <link> du hook useThemeCSS.
    const activeServiceTheme = getEffectiveThemes(config.themes)
      .find((t) => t.id === (config.activeThemeId || 'default'));
    // Le DISTRIBUTEUR reçoit la MÊME forme de cadre, avec ses propres sélecteurs : le CSS
    // généré cible les classes de /display (`.service-counts-grid` / `.service-count-item`),
    // on les remappe vers celles de la borne. C'est le remappage que fait déjà le
    // distributeur Electron (apps/distributor/main.js) — sans lui, la forme choisie en
    // console (livre, ovale, raisins…) ne s'appliquait qu'à l'écran d'attente.
    const serviceCardCss = currentPanel === 'display'
      ? generateServiceCardCss(activeServiceTheme)
      : currentPanel === 'distributor'
        // Deux classes, pas une : `styles/custom.css` cible `.distributor-panel
        // .service-button` et l'emporterait sinon sur le rayon et le remplissage du
        // cadre — la forme choisie en console n'aurait été appliquée qu'à moitié
        // (le masque passait, la géométrie non).
        ? generateServiceCardCss(activeServiceTheme)
            .replace(/\.service-counts-grid/g, '.distributor-panel .service-buttons')
            .replace(/\.service-count-item/g, '.distributor-panel .service-button')
        : '';

    // Part de largeur du panneau des tickets (%). Réglable console ; bornée pour qu'une
    // saisie farfelue ne fasse pas disparaître l'un des deux panneaux.
    const partTickets = Math.min(60, Math.max(15, Number(config?.styles?.display?.ticketsPanelPercent) || 33));

    // Disposition des deux panneaux. Les thèmes-fichiers portent un point de rupture
    // responsive (`@media (max-width: 992px) { .display-main { flex-direction: column } }`),
    // pensé pour un téléphone. Or un écran d'ARRIVÉE n'est jamais un téléphone : c'est une
    // TV ou un moniteur, et la WebView d'une TV Android déclare souvent une largeur CSS
    // inférieure à ce seuil. La liste des tickets basculait alors en bas, pleine largeur,
    // alors que l'exploitant l'attend à droite. On tranche donc explicitement, et le
    // défaut est « côte à côte » — ce que réclame un écran d'attente.
    const dispo = config?.styles?.display?.panelsLayout || 'row';

    // Colonnes de la disposition « Grille », reprises du MÊME réglage que les fiches
    // classiques (thème actif → serviceCard). Un nombre fixe donne des pistes qui peuvent se
    // serrer ; « auto » conserve le remplissage adaptatif d'après la largeur mini.
    const carte = (getEffectiveThemes(config.themes).find((t) => t.id === (config.activeThemeId || 'default'))?.serviceCard) || {};
    const colDemandees = String(carte.columns ?? 'auto').trim();
    const largeurMini = String(carte.minWidth || '240px').trim();
    const grilleCols = /^\d+$/.test(colDemandees)
      ? `repeat(${colDemandees}, minmax(0, 1fr))`
      : `repeat(auto-fit, minmax(${largeurMini}, 1fr))`;
    const grilleGap = String(carte.gap || '2rem').trim();

    // Styles spécifiques pour le panneau d'affichage (écran)
    const displayCssRules = currentPanel === 'display' ? `
      /* Styles pour l'écran d'affichage */
      .display-panel {
        --primary-bg-color: var(--primary-color, #2196F3);
        --secondary-bg-color: rgba(255, 255, 255, 0.15);
        --text-color: white;
        --ticket-bg-color: white;
        --ticket-number-color: var(--primary-color, #2196F3);
        --counter-bg-color: var(--primary-color, #2196F3);
        --counter-text-color: white;
        --header-bg-color: rgba(0, 0, 0, 0.1);
        --footer-bg-color: rgba(0, 0, 0, 0.2);
      }

      /* Container principal */
      .display-container {
        height: 100vh;            /* hauteur EXACTE de l'écran (pas min-height : évite que le
                                    contenu pousse le conteneur au-delà du viewport → scroll) */
        display: flex;
        flex-direction: column;
        background-color: var(--primary-bg-color);
        color: var(--text-color);
        overflow: hidden;
      }

      /* En-tête avec logo et heure — toujours visible (ne se rétracte pas) */
      .display-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 1rem;
        background-color: var(--header-bg-color);
        flex-shrink: 0;
      }

      .display-logo-container {
        display: flex;
        align-items: center;
      }

      .display-logo {
        background-color: white;
        border-radius: 8px;
        padding: 0.5rem;
        margin-right: 1rem;
        width: auto;
        height: 3.5rem;
      }

      .display-agency-info h1 {
        font-size: 1.5rem;
        font-weight: bold;
        margin: 0;
      }

      .display-agency-info p {
        font-size: 1rem;
        opacity: 0.8;
        margin: 0;
      }

      .display-datetime {
        text-align: right;
      }

      .display-time {
        font-size: 2rem;
        font-weight: bold;
      }

      .display-date {
        font-size: 1rem;
      }

      /* Contenu principal — remplit l'espace entre header et footer ; min-height:0 +
         overflow permet le scroll INTERNE (au lieu de pousser la page). */
      .display-main {
        display: flex;
        ${dispo === 'auto' ? '' : `flex-direction: ${dispo === 'column' ? 'column' : 'row'};`}
        flex: 1;
        min-height: 0;
        overflow: hidden;
        padding: 1rem;
      }

      /* Panneau de gauche — la part RESTANTE de la largeur (réglable depuis la console).
         min-width:0 est indispensable : un element flex vaut min-width:auto par defaut, donc
         il REFUSE de descendre sous la largeur minimale de son contenu. Sur un grand ecran de
         bureau la somme des deux minimums tient ; sur la WebView d'une TV Android elle
         deborde, la rangee casse, et le panneau des tickets part hors cadre. Le rapport ne
         tenait donc que par la taille de l'ecran. */
      .display-welcome-panel {
        flex: 0 0 ${100 - partTickets}%;
        min-width: 0;
        background-color: var(--secondary-bg-color);
        border-radius: 8px;
        padding: 2rem;
        margin-right: 1rem;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        text-align: center;
        min-height: 0;
      }

      .welcome-message-ar {
        font-size: 2.5rem;
        margin-bottom: 1rem;
        font-family: 'Amiri', serif;
      }

      .welcome-message-fr {
        font-size: 1.8rem;
      }

      /* Panneau de droite — part de largeur REGLABLE depuis la console (defaut un tiers).
         L'ancienne largeur fixe de 25rem contredisait flex (la base flex l'emporte dans l'axe
         principal) et n'ajoutait qu'un plancher invisible qui participait au debordement. */
      .display-tickets-panel {
        flex: 0 0 ${partTickets}%;
        min-width: 0;
        ${dispo === 'column' ? '' : 'width: auto;'}
        background-color: var(--secondary-bg-color);
        border-radius: 8px;
        padding: 1rem;
        display: flex;
        flex-direction: column;
        min-height: 0;   /* laisse .tickets-list scroller en interne */
      }

      .tickets-header {
        font-size: 1.5rem;
        font-weight: bold;
        text-align: center;
        margin-bottom: 1rem;
        padding-bottom: 0.5rem;
        border-bottom: 1px solid rgba(255, 255, 255, 0.2);
      }

      .tickets-list {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
        overflow-y: auto;
      }

      .ticket-item {
        background-color: var(--ticket-bg-color);
        border-radius: 8px;
        padding: 1rem;
        display: flex;
        justify-content: space-between;
        align-items: center;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      }

      .ticket-number {
        font-size: 2rem;
        font-weight: bold;
        color: var(--ticket-number-color);
      }

      .counter-badge {
        width: 2.5rem;
        height: 2.5rem;
        display: flex;
        justify-content: center;
        align-items: center;
        background-color: var(--counter-bg-color);
        color: var(--counter-text-color);
        border-radius: 8px;
        font-weight: bold;
        font-size: 1.25rem;
      }

      /* Pied de page */
      .display-footer {
        background-color: var(--footer-bg-color);
        padding: 0.75rem;
        text-align: center;
        flex-shrink: 0;   /* toujours visible en bas */
      }
      /* Noms de service (AR/EN/FR) — taille PAR LANGUE pilotée par la config (onglet Styles
         → serviceNameSize + serviceNameScale{Ar,En,Fr}). Graisse UNIFORME : aucune langue
         n'est privilégiée par défaut ; c'est l'échelle qui donne la hiérarchie voulue. */
      .service-count-item .service-name {
        font-size: calc(var(--service-name-size, 1.4rem) * var(--sn-scale-fr, 1));
        font-weight: 600;
        line-height: 1.15;
      }
      .service-count-item .service-name-ar {
        font-size: calc(var(--service-name-size, 1.4rem) * var(--sn-scale-ar, 0.8));
        opacity: 1;                 /* était trop pâle → arabe presque invisible */
        margin-top: 0.2rem;
        font-family: 'Amiri', serif;
        font-weight: 600;
      }
      .service-count-item .service-name-en {
        font-size: calc(var(--service-name-size, 1.4rem) * var(--sn-scale-en, 0.65));
        opacity: 0.95;
        margin-top: 0.1rem;
        font-weight: 600;
      }
      .service-count-item .waiting-text {
        font-size: calc(var(--service-name-size, 1.4rem) * 0.6);
        opacity: 0.9;
      }
      /* La GRILLE de services (disposition « Grille ») obéit désormais aux MÊMES réglages que
         les fiches classiques : colonnes, écart, largeur mini. Elle ne les lisait pas — le
         champ « Colonnes » de la console n'avait donc aucune prise sur ce mode — et son CSS
         statique la faisait retomber à UNE carte par ligne sous 768 px, largeur que déclare
         couramment la WebView d'une TV. Sur un mur d'affichage, une carte par ligne est
         inexploitable. Ce bloc est injecté après la feuille du composant, donc il l'emporte. */
      .services-grid {
        grid-template-columns: ${grilleCols};
        gap: ${grilleGap};
      }

      /* Même pilotage pour la GRILLE de service (ServiceGrid) — override du CSS statique. */
      .service-grid-header .service-grid-name-ar { font-size: calc(var(--service-name-size, 1.4rem) * var(--sn-scale-ar, 0.8)); font-weight: 600; }
      .service-grid-header .service-grid-name-en { font-size: calc(var(--service-name-size, 1.4rem) * var(--sn-scale-en, 0.65)); font-weight: 600; }
      .service-grid-header .service-grid-name    { font-size: calc(var(--service-name-size, 1.4rem) * var(--sn-scale-fr, 1)); font-weight: 600; }
    ` : '';
    
    // Styles spécifiques pour le guichet
    const counterCssRules = currentPanel === 'counter' ? `
      /* La rangée d'actions ne passait pas à la ligne : \`.action-buttons\` est un flex
         SANS \`flex-wrap\`, et ses boutons portent \`flex: 1\`. Or un élément flex ne se
         réduit pas sous sa largeur de contenu : à cinq boutons — Appeler, Terminer,
         Terminer + Suivant, Rappeler, Pause — la rangée débordait du panneau et les deux
         derniers sortaient du cadre. « Rappeler » devenait donc invisible dès qu'un
         ticket était en service, c'est-à-dire exactement quand il sert.
         Le repli en colonne du thème n'intervient qu'en dessous de 480 px, largeur bien
         plus étroite que celle d'un guichet à l'écran. */
      .counter-panel .action-buttons {
        flex-wrap: wrap;
      }
      .counter-panel .action-button {
        min-width: 8rem;
      }

      /* Styles globaux pour le guichet */
      .counter-panel {
        --counter-primary-color: var(--primary-color, #2196F3);
        --counter-bg-color: #f5f5f5;
        --card-bg-color: white;
        --header-bg-color: var(--primary-color, #2196F3);
        --header-text-color: white;
        --status-available-color: #4CAF50;
        --status-busy-color: #F44336;
        --ticket-color: #3E2723;
        --call-button-color: #4CAF50;
        --complete-button-color: #FF9800;
      }

      /* Container principal */
      .counter-container {
        min-height: 100vh;
        background-color: var(--counter-bg-color);
        padding: 1rem;
        font-family: var(--font-family, 'Arial, sans-serif');
      }

      /* Carte principale du guichet */
      .counter-card {
        max-width: 420px;
        margin: 0 auto;
        background-color: var(--card-bg-color);
        border-radius: 8px;
        box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
        overflow: hidden;
      }

      /* En-tête du guichet */
      .counter-header {
        background-color: var(--header-bg-color);
        color: var(--header-text-color);
        padding: 1rem;
        text-align: center;
      }

      .counter-title {
        font-size: 1.25rem;
        font-weight: bold;
        margin: 0;
      }

      .counter-status {
        font-size: 0.9rem;
        margin-top: 0.25rem;
      }

      /* Section du ticket actuel */
      .current-ticket-section {
        padding: 1.5rem 1rem;
        border: 1px solid #e0e0e0;
        border-radius: 8px;
        margin-bottom: 1rem;
      }

      .current-ticket-label {
        font-size: 1rem;
        color: #757575;
        margin-bottom: 0.5rem;
      }

      .current-ticket-number {
        font-size: 3rem;
        font-weight: bold;
        color: var(--ticket-color);
        text-align: center;
      }
    ` : '';
    
    // Styles spécifiques pour le distributeur de tickets
    const distributorCssRules = currentPanel === 'distributor' ? `
      /* Styles globaux pour le distributeur */
      .distributor-panel {
        --distributor-primary-color: var(--primary-color, #2196F3);
        --distributor-bg-color: var(--primary-color, #2196F3);
        --header-bg-color: white;
        --footer-bg-color: var(--primary-color, #2196F3);
        --footer-darker-bg-color: rgba(0, 0, 0, 0.1);
        /* Couleur des cadres de service : ce réglage existait depuis toujours dans la config
           (styles.distributor.serviceButtonColor) mais n'était CÂBLÉ nulle part — la valeur
           était écrite en dur ici, et la modifier en console ne changeait rien.
           L'arrondi, lui, reste alimenté par la config mais n'a le dernier mot que si aucune
           forme de cadre n'est appliquée : la « Forme des carrés » choisie en console écrit
           toujours son propre border-radius, et elle est injectée après ce bloc. Il n'est
           donc PAS proposé dans la console — un réglage sans effet vaut moins que rien. */
        --service-button-bg: ${panelStyles.serviceButtonColor || 'white'};
        --service-button-text: #333;
        --service-button-border-radius: ${panelStyles.serviceButtonRadius || '8px'};
        --welcome-bg-color: white;
      }

      /* Container principal */
      .distributor-container {
        min-height: 100vh;
        display: flex;
        flex-direction: column;
        background-color: var(--distributor-bg-color);
        font-family: var(--font-family, 'Arial, sans-serif');
      }

      /* En-tête du distributeur */
      .distributor-header {
        background-color: var(--header-bg-color);
        padding: 1rem;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      }

      .distributor-header-content {
        display: flex;
        justify-content: space-between;
        align-items: center;
        max-width: 1200px;
        margin: 0 auto;
      }

      .distributor-logo-container {
        display: flex;
        align-items: center;
      }

      .distributor-logo {
        height: 3.5rem;
        width: auto;
        margin-right: 1rem;
      }

      /* Welcome panel */
      .welcome-panel {
        background-color: var(--welcome-bg-color);
        border-radius: 12px;
        padding: 1.5rem;
        max-width: 800px;
        width: 100%;
        margin-bottom: 2rem;
        text-align: center;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
      }

      .welcome-title-ar {
        font-size: 2rem;
        margin-bottom: 0.5rem;
        font-family: 'Amiri', serif;
      }

      .welcome-title-fr {
        font-size: 1.5rem;
        color: #333;
      }
      
      /* Service buttons */
      .service-button {
        background-color: var(--service-button-bg);
        border-radius: var(--service-button-border-radius);
        padding: 1.5rem;
        display: flex;
        justify-content: space-between;
        align-items: center;
        cursor: pointer;
        text-align: left;
        border: none;
        box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
        transition: all 0.2s ease;
        position: relative;
        overflow: hidden;
        margin-bottom: 1rem;
      }

      .service-button::before {
        content: '';
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        width: 8px;
        background-color: var(--service-color, var(--distributor-primary-color));
      }

      /* ⚠ SPÉCIFICITÉ — toutes les règles qui suivent sont préfixées \`.distributor-panel\`
         (classe posée sur <body> plus haut). \`styles/custom.css\` porte en effet un
         \`.distributor-panel .service-button { text-align: left; background-color: white }\`
         à DEUX classes : une règle à une seule classe perd contre lui quel que soit
         l'ordre d'injection. C'est ce qui rendait la couleur des cadres inopérante et
         gardait le texte aligné à gauche alors que la feuille injectée demandait le centre.

         Disposition : GRILLE, pilotée par les MÊMES réglages de console que /display
         (thème actif → serviceCard : colonnes, écart, largeur mini). Le thème-fichier
         empile les cadres sur une colonne et pose le nom FR à gauche, le nom AR à droite :
         cette mise en page ne peut pas rendre un ordre de langues réglable — elle n'a que
         deux emplacements, et ils sont figés. La borne adopte donc la grille de cadres
         centrés du distributeur Electron, qui est déjà la référence. */
      .distributor-panel .service-buttons {
        display: grid;
        grid-template-columns: ${grilleCols};
        gap: ${grilleGap};
      }
      /* Le conteneur était borné à 800 px : à trois services, des cadres de 250 px, où un
         nom arabe à l'échelle réglée pour l'écran d'attente se coupe en deux lignes et
         déborde. Le distributeur Electron, lui, donne toute la largeur à ses cadres — c'est
         cette respiration qui fait que le même réglage y est lisible. */
      .distributor-panel .service-selection {
        max-width: 1200px;
      }
      .distributor-panel .service-button {
        flex-direction: column;
        justify-content: center;
        align-items: stretch;
        text-align: center;
        min-height: 120px;
        margin-bottom: 0;
        background-color: var(--service-button-bg);
      }
      .distributor-panel .service-button .service-info {
        margin-left: 0;
        width: 100%;
      }

      /* Noms de service (AR/EN/FR) — MÊMES règles que les cadres de /display, alimentées
         par les mêmes variables (issues de styles.display, cf. \`snStyles\`). La borne et
         l'écran d'attente écrivent donc le nom d'un service de la même façon.
         \`text-align\` est reposé sur CHAQUE ligne : en RTL, un texte qui hérite d'un
         alignement se recale à droite, et les trois langues d'un même cadre ne seraient
         alors pas sur le même axe. */
      /* Écart vertical IDENTIQUE sur les trois langues : l'ordre est réglable en console,
         donc aucune ne peut être « celle du dessus » ni « celle du dessous ». Un écart posé
         sur une seule d'entre elles se déplacerait avec elle au prochain changement d'ordre. */
      .distributor-panel .service-button .service-name {
        font-size: calc(var(--service-name-size, 1.8rem) * var(--sn-scale-fr, 1));
        font-weight: 600;
        line-height: 1.2;
        margin-top: 0.35rem;
        text-align: center;
      }
      .distributor-panel .service-button .service-name-ar {
        font-size: calc(var(--service-name-size, 1.8rem) * var(--sn-scale-ar, 0.8));
        font-family: 'Amiri', serif;
        font-weight: 600;
        line-height: 1.2;
        margin-top: 0.35rem;
        text-align: center;
      }
      .distributor-panel .service-button .service-name-en {
        font-size: calc(var(--service-name-size, 1.8rem) * var(--sn-scale-en, 0.65));
        opacity: 0.95;
        font-weight: 600;
        line-height: 1.2;
        margin-top: 0.35rem;
        text-align: center;
      }
      .distributor-panel .service-button .service-description {
        font-size: calc(var(--service-name-size, 1.8rem) * 0.45);
        opacity: 0.8;
        margin-top: 0.15rem;
        text-align: center;
      }
    ` : '';
    
    // Styles pour l'accessibilité
    const accessibilityCssRules = `
      /* Mode contraste élevé si activé */
      ${config.accessibility?.highContrastMode ? `
        body.high-contrast-mode {
          background: #000;
          color: #fff;
        }
        
        body.high-contrast-mode .bg-white {
          background: #000 !important;
          color: #fff !important;
        }
        
        body.high-contrast-mode .text-gray-600,
        body.high-contrast-mode .text-gray-700,
        body.high-contrast-mode .text-gray-800 {
          color: #fff !important;
        }
      ` : ''}
      
      /* Mode texte large si activé */
      ${config.accessibility?.largeTextMode ? `
        body.large-text-mode {
          font-size: 120%;
        }
        
        body.large-text-mode h1 {
          font-size: 2.5rem;
        }
        
        body.large-text-mode h2 {
          font-size: 2rem;
        }
        
        body.large-text-mode h3 {
          font-size: 1.75rem;
        }
      ` : ''}
      
      /* Support pour les langues RTL (Arabe) */
      .rtl {
        direction: rtl;
      }
    `;
    // Combine tous les styles
    // serviceCardCss EN DERNIER (avant l'accessibilité) → il a le dernier mot sur les
    // carrés, aussi bien sur /display que sur /distributor. Il était placé avant le bloc
    // du distributeur, qui lui aurait repris la main sur l'arrondi des cadres et annulé
    // la forme choisie en console.
    const allStyles = cssRules + displayCssRules + counterCssRules + distributorCssRules + serviceCardCss + accessibilityCssRules ;
    
    // Applique les styles
    styleSheet.innerHTML = allStyles;
    
    // Applique les classes d'accessibilité au body
    if (config.accessibility?.highContrastMode) {
      document.body.classList.add('high-contrast-mode');
    } else {
      document.body.classList.remove('high-contrast-mode');
    }
    
    if (config.accessibility?.largeTextMode) {
      document.body.classList.add('large-text-mode');
    } else {
      document.body.classList.remove('large-text-mode');
    }
    
    // Nettoyage lors du démontage du composant
    return () => {
      if (styleSheet && styleSheet.parentNode) {
        styleSheet.parentNode.removeChild(styleSheet);
      }
    };
  }, [config, loading, location]);

  return <>{children}</>;
};

export default DynamicStylesProvider;